import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button } from "@mui/material";
import {
  FlashbackSectionTabs,
  type FlashbackDashboardLocationState,
  type FlashbackMainTab,
} from "../../components/flashbackSections/FlashbackSectionTabs.tsx";
import { FlashbackHistoryPanel } from "../../components/flashbackSections/FlashbackHistoryPanel.tsx";
import { Toolbar } from "../../components/flashbackSections/Toolbar.tsx";
import { CameraGrid } from "../../components/flashbackSections/CameraGrid.tsx";
import { StatePanel } from "../../components/flashbackSections/StatePanel.tsx";
import { useCameras } from "../../hooks/flashbackHooks/useCameras.ts";
import { useDebounced } from "../../hooks/flashbackHooks/useDebounced.ts";
import { camerasApi } from "../../api/flashbackApi/cameras.ts";
import { useDashboardStore } from "../../store/flashbackStore/dashboardStore.ts";
import type { Camera } from "../../api/flashbackApi/types.ts";

const PAGE_SIZE = 48;

function readTabFromLocation(state: unknown): FlashbackMainTab | null {
  const tab = (state as FlashbackDashboardLocationState | null)?.tab;
  return tab === "history" || tab === "live" ? tab : null;
}

export default function Dashboard(): React.ReactElement {
  const navigate = useNavigate();
  const location = useLocation();
  const [mainTab, setMainTab] = useState<FlashbackMainTab>(
    () => readTabFromLocation(location.state) ?? "live",
  );

  useEffect(() => {
    const tab = readTabFromLocation(location.state);
    if (tab) setMainTab(tab);
  }, [location.key, location.state]);
  const { filters, layout } = useDashboardStore();
  const debouncedSearch = useDebounced(filters.search, 250);

  const cameraQuery = useMemo(() => {
    const paging = { page: 1, pageSize: PAGE_SIZE };
    if (mainTab === "live") {
      return {
        ...paging,
        search: debouncedSearch.trim() || undefined,
        sortBy: "name" as const,
        sortOrder: "asc" as const,
      };
    }
    return {
      ...paging,
      sortBy: "name" as const,
      sortOrder: "asc" as const,
    };
  }, [mainTab, filters, debouncedSearch]);

  const { data, isLoading, error, refresh, setOptimistic } =
    useCameras(cameraQuery);

  const handleToggleStatus = useCallback(
    async (camera: Camera) => {
      const next = camera.status === "active" ? "inactive" : "active";
      setOptimistic((prev) => ({
        ...prev,
        items: prev.items.map((c) =>
          c.id === camera.id ? { ...c, status: next } : c,
        ),
      }));
      try {
        await camerasApi.setStatus(camera.id, next);
      } catch {
        refresh();
      }
    },
    [refresh, setOptimistic],
  );

  const items = data?.items ?? [];

  const outlineBtnSx = {
    borderRadius: "6px",
    border: "1px solid var(--app-border)",
    backgroundColor: "var(--app-surface)",
    color: "var(--app-text-primary)",
    fontSize: 14,
    textTransform: "none" as const,
    px: 1.5,
    py: 0.75,
    "&:hover": {
      borderColor: "var(--app-primary)",
      backgroundColor: "var(--app-surface)",
    },
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        height: "100%",
        color: "var(--app-text-primary)",
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          backgroundColor: "#f2f2f2",
        }}
      >
        <FlashbackSectionTabs value={mainTab} onChange={setMainTab} />

        {mainTab === "live" ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              px: 2,
              pb: 2,
              backgroundColor: "#F0F0F0",
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                backgroundColor: "#FFFFFF",
                border: "1px solid #E0E0E0",
                borderRadius: "12px",
                p: { xs: 2, sm: 2.5 },
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
                overflow: "hidden",
              }}
            >
              <Toolbar />

              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  overflowX: "hidden",
                  WebkitOverflowScrolling: "touch",
                }}
              >
                {error ? (
                  <StatePanel
                    variant="error"
                    title="Couldn’t load cameras"
                    description={error}
                    action={
                      <Button
                        type="button"
                        variant="text"
                        onClick={refresh}
                        sx={outlineBtnSx}
                      >
                        Retry
                      </Button>
                    }
                  />
                ) : isLoading && items.length === 0 ? (
                  <StatePanel
                    title="Loading cameras…"
                    description="Fetching the live grid from the backend."
                  />
                ) : items.length === 0 ? (
                  <StatePanel
                    variant="empty"
                    title="No cameras yet"
                    description="No cameras are available for this account. Adjust filters or check back later."
                  />
                ) : (
                  <CameraGrid
                    cameras={items}
                    layout={layout}
                    onToggleStatus={handleToggleStatus}
                    onOpenPlayback={(camera) =>
                      navigate(`/cameras/${camera.id}/playback`, {
                        state: { fromTab: "live" },
                      })
                    }
                  />
                )}
              </Box>
            </Box>
          </Box>
        ) : (
          <FlashbackHistoryPanel
            cameras={items}
            isLoadingCameras={isLoading}
            camerasError={error}
            onRefreshCameras={refresh}
          />
        )}
      </Box>

    </Box>
  );
}
